import PropTypes from "prop-types";
import DropdownSelect from "./DropdownSelect";

const ALPHA_FALLBACK_KEY = "#";

const flattenOptions = (options = []) =>
  options.flatMap(opt => Array.isArray(opt.options) ? opt.options : [opt]);

const resolveSelectedOption = (value, options = []) => {
  const flat = flattenOptions(options);
  if (!value) return null;

  if (typeof value === "object" && value?.value != null) {
    return flat.find((option) => String(option.value) === String(value.value)) ?? value;
  }

  return flat.find((option) => String(option.value) === String(value)) ?? null;
};

const getFirstLetterKey = (label = "") => {
  const firstChar = String(label).trim().charAt(0).toUpperCase();
  return /[A-Z]/.test(firstChar) ? firstChar : ALPHA_FALLBACK_KEY;
};

// Buckets options (or, when already category-grouped, whole categories) under
// A-Z letter groups so react-select's native 1-level grouping can render an
// alphabetical index. Category-grouped input keeps its category header +
// child items nested together inside the letter their category name starts with.
const buildAlphabeticalGroups = (options = []) => {
  const isCategoryGrouped = options.some((opt) => Array.isArray(opt.options));
  const buckets = {};

  const addToBucket = (letter, items) => {
    if (!buckets[letter]) buckets[letter] = [];
    buckets[letter].push(...items);
  };

  const sortByLabel = (items) =>
    [...items].sort((a, b) => String(a.label).localeCompare(String(b.label)));

  if (isCategoryGrouped) {
    sortByLabel(options).forEach((category) => {
      const categoryItems = category.options || [];
      if (!categoryItems.length) return;
      const headerOption = {
        value: `__group_header__${category.label}`,
        label: category.label,
        isCategoryHeader: true,
        isDisabled: true,
        childLabels: categoryItems.map((item) => item.label),
      };
      addToBucket(getFirstLetterKey(category.label), [headerOption, ...categoryItems]);
    });
  } else {
    sortByLabel(options).forEach((option) => {
      addToBucket(getFirstLetterKey(option.label), [option]);
    });
  }

  return Object.keys(buckets)
    .sort()
    .map((letter) => ({ label: letter, options: buckets[letter] }));
};

const DropDown = (props) => {
  const flatOptions = props.isGrouped ? flattenOptions(props.options) : props.options;
  const defaultSelectedOption = resolveSelectedOption(props.value, props.options);

  const defaultSelectedOptionMultiSelect = Array.isArray(props.value)
    ? flatOptions.filter((option) => props.value.map(String).includes(String(option.value)))
    : [];

  const displayOptions = props.isAlphabeticallyGrouped
    ? buildAlphabeticalGroups(props.options)
    : props.options;

  return (
    <span className="d-inline-block custom-input">
      <DropdownSelect
        placeholder={props.placeholder}
        isMultiSelect={props.isMultiSelect}
        isSearchable={props.isSearchable}
        isClearable={props.isClearable}
        isAlphabeticallyGrouped={props.isAlphabeticallyGrouped}
        optionsValue={displayOptions}
        value={props.isMultiSelect ? defaultSelectedOptionMultiSelect : defaultSelectedOption}
        handleDropdownChange={props.onChange}
        handleDropdownBlur={props.onBlur}
        isDropdownDisabled={props.isDisabled}
        isLoading={props.isLoading}
        menuPortalTarget={document.body}
        menuPosition="absolute"
        maxMenuHeight={props.maxMenuHeight}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }} />
    </span>
  );
};
DropDown.propTypes = {
  placeholder: PropTypes.string,
  isMultiSelect: PropTypes.bool,
  isGrouped: PropTypes.bool,
  isAlphabeticallyGrouped: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  isDisabled: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isClearable: PropTypes.bool,
  isLoading: PropTypes.bool,
  maxMenuHeight: PropTypes.number
};

export default DropDown;
